import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Layers, Mail, Key, ArrowRight, CheckCircle, AlertCircle, Search } from 'lucide-react';

const STATUSES = ['Client Data', 'Appraisal Booked', 'Quote Sent', 'Accepted', 'Declined', 'Work Booked', 'Work In Progress', 'Completed', 'Invoiced', 'Overdue', 'Paid', 'Closed · Lost', 'Closed · Bad Debt'];

export default function BulkOperationsPage() {
  const { api } = useAuth();
  const [tab, setTab] = useState('status');
  const [appraisals, setAppraisals] = useState([]);
  const [clients, setClients] = useState([]);
  const [selectedApps, setSelectedApps] = useState([]);
  const [selectedClients, setSelectedClients] = useState([]);
  const [newStatus, setNewStatus] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [result, setResult] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [searchApp, setSearchApp] = useState('');
  const [searchCl, setSearchCl] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const [a, c] = await Promise.all([api.get('/appraisals?limit=100'), api.get('/clients?limit=100')]);
    setAppraisals(a.data.items || []);
    setClients(c.data.items || []);
    setLoading(false);
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const toggleApp = (id) => setSelectedApps(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const toggleClient = (id) => setSelectedClients(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const selectAllApps = () => setSelectedApps(filteredApps.map(a => a._id));
  const selectAllClients = () => setSelectedClients(filteredClients.map(c => c._id));
  const clearAll = () => { setSelectedApps([]); setSelectedClients([]); };

  const bulkStatus = async () => {
    if (!selectedApps.length || !newStatus) return;
    setProcessing(true); setResult(null);
    const { data } = await api.post('/bulk/status', { appraisal_ids: selectedApps, new_status: newStatus });
    setResult({ type: 'status', ...data });
    setProcessing(false); setSelectedApps([]); load();
  };

  const bulkEmail = async () => {
    if (!selectedClients.length || !emailSubject) return;
    setProcessing(true); setResult(null);
    const { data } = await api.post('/bulk/email', { client_ids: selectedClients, subject: emailSubject, body: emailBody });
    setResult({ type: 'email', ...data });
    setProcessing(false); setSelectedClients([]);
  };

  const bulkPortal = async () => {
    if (!selectedClients.length) return;
    setProcessing(true); setResult(null);
    const { data } = await api.post('/bulk/generate-portal', { client_ids: selectedClients });
    setResult({ type: 'portal', ...data });
    setProcessing(false); setSelectedClients([]);
  };

  const filteredApps = appraisals.filter(a => !searchApp || (a.client_name || '').toLowerCase().includes(searchApp.toLowerCase()) || (a.quote_type_ref || '').toLowerCase().includes(searchApp.toLowerCase()));
  const filteredClients = clients.filter(c => !searchCl || `${c.name_first} ${c.name_last}`.toLowerCase().includes(searchCl.toLowerCase()) || (c.email || '').toLowerCase().includes(searchCl.toLowerCase()));

  const tabs = [
    { key: 'status', label: 'Bulk Status Change', icon: Layers },
    { key: 'email', label: 'Bulk Email', icon: Mail },
    { key: 'portal', label: 'Bulk Portal Access', icon: Key },
  ];

  return (
    <div data-testid="bulk-operations-page" className="space-y-4">
      <div>
        <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 font-[Manrope]">Bulk Operations</h1>
        <p className="text-sm text-slate-500 mt-1">Mass updates, emails, and portal access generation</p>
      </div>
      {result && (
        <div className={`p-4 rounded-md text-sm border flex items-center gap-2 ${result.type === 'status' ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-green-50 text-green-700 border-green-200'}`} data-testid="bulk-result">
          <CheckCircle size={16} />
          {result.type === 'status' && `Updated ${result.updated}/${result.total} appraisals`}
          {result.type === 'email' && `Sent ${result.sent} emails (${result.failed} failed) of ${result.total}`}
          {result.type === 'portal' && `Generated ${result.generated} portal access codes of ${result.total}`}
        </div>
      )}
      <div className="flex gap-1 border-b border-[#E2E2DF]">
        {tabs.map(t => <button key={t.key} data-testid={`bulk-tab-${t.key}`} onClick={() => { setTab(t.key); setResult(null); }} className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-all ${tab === t.key ? 'border-[#D35400] text-[#D35400]' : 'border-transparent text-slate-500'}`}><t.icon size={14} />{t.label}</button>)}
      </div>

      {tab === 'status' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 bg-white border border-[#E2E2DF] rounded-md p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-semibold text-slate-800">{selectedApps.length} selected</span>
              <div className="flex gap-2"><button onClick={selectAllApps} className="text-xs text-[#D35400] font-semibold">Select All</button><button onClick={clearAll} className="text-xs text-slate-400">Clear</button></div>
            </div>
            <div className="relative mb-3"><Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" /><input value={searchApp} onChange={e => setSearchApp(e.target.value)} placeholder="Filter appraisals..." className="w-full pl-9 pr-3 py-2 border border-[#E2E2DF] rounded-md text-sm" /></div>
            <div className="max-h-96 overflow-y-auto space-y-1">
              {filteredApps.map(a => (
                <label key={a._id} className={`flex items-center gap-3 p-2 rounded cursor-pointer transition-colors ${selectedApps.includes(a._id) ? 'bg-[#D35400]/5' : 'hover:bg-slate-50'}`}>
                  <input type="checkbox" checked={selectedApps.includes(a._id)} onChange={() => toggleApp(a._id)} className="rounded" />
                  <div className="flex-1 min-w-0"><p className="text-sm text-slate-800 truncate">{a.client_name || 'Unknown'} - {a.quote_type_ref || 'N/A'}</p></div>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 flex-shrink-0">{a.appraisal_status}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="bg-white border border-[#E2E2DF] rounded-md p-4 space-y-4 h-fit">
            <h3 className="text-base font-semibold text-slate-800">Change Status To</h3>
            <div className="grid grid-cols-2 gap-2">
              {STATUSES.map(s => <button key={s} onClick={() => setNewStatus(s)} className={`text-xs px-2 py-1.5 rounded-md border text-left transition-all ${newStatus === s ? 'border-[#D35400] bg-[#D35400]/5 text-[#D35400] font-semibold' : 'border-[#E2E2DF] text-slate-500 hover:border-slate-300'}`}>{s}</button>)}
            </div>
            <button data-testid="bulk-status-apply" onClick={bulkStatus} disabled={processing || !selectedApps.length || !newStatus} className="w-full py-2.5 bg-[#D35400] hover:bg-[#A04000] text-white rounded-md text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-2"><Layers size={14} /> {processing ? 'Updating...' : `Update ${selectedApps.length} Appraisals`}</button>
            {newStatus === 'Quote Sent' && <p className="text-xs text-blue-600 bg-blue-50 p-2 rounded">Portal access will be auto-generated and emailed to clients.</p>}
          </div>
        </div>
      )}

      {tab === 'email' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="bg-white border border-[#E2E2DF] rounded-md p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-semibold text-slate-800">{selectedClients.length} selected</span>
              <div className="flex gap-2"><button onClick={selectAllClients} className="text-xs text-[#D35400] font-semibold">Select All</button><button onClick={clearAll} className="text-xs text-slate-400">Clear</button></div>
            </div>
            <div className="relative mb-3"><Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" /><input value={searchCl} onChange={e => setSearchCl(e.target.value)} placeholder="Filter clients..." className="w-full pl-9 pr-3 py-2 border border-[#E2E2DF] rounded-md text-sm" /></div>
            <div className="max-h-96 overflow-y-auto space-y-1">
              {filteredClients.map(c => (
                <label key={c._id} className={`flex items-center gap-3 p-2 rounded cursor-pointer ${selectedClients.includes(c._id) ? 'bg-[#D35400]/5' : 'hover:bg-slate-50'}`}>
                  <input type="checkbox" checked={selectedClients.includes(c._id)} onChange={() => toggleClient(c._id)} className="rounded" />
                  <div className="flex-1 min-w-0"><p className="text-sm text-slate-800 truncate">{c.name_first} {c.name_last}</p><p className="text-[10px] text-slate-400">{c.email || 'No email'}</p></div>
                </label>
              ))}
            </div>
          </div>
          <div className="lg:col-span-2 bg-white border border-[#E2E2DF] rounded-md p-4 space-y-4">
            <h3 className="text-base font-semibold text-slate-800">Compose Email</h3>
            <p className="text-xs text-slate-400">Use placeholders: {"{{client_name}}"}, {"{{client_first_name}}"}, {"{{client_email}}"}</p>
            <div><label className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-1.5">Subject</label><input data-testid="bulk-email-subject" value={emailSubject} onChange={e => setEmailSubject(e.target.value)} className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm" placeholder="e.g., Important Update from Roofs To Remedy Ltd" /></div>
            <div><label className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-1.5">Body</label><textarea data-testid="bulk-email-body" value={emailBody} onChange={e => setEmailBody(e.target.value)} rows={8} className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm" placeholder={"Dear {{client_name}},\n\n..."} /></div>
            <button data-testid="bulk-email-send" onClick={bulkEmail} disabled={processing || !selectedClients.length || !emailSubject} className="px-5 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-md text-sm font-semibold disabled:opacity-50 flex items-center gap-2"><Mail size={14} /> {processing ? 'Sending...' : `Send to ${selectedClients.length} Clients`}</button>
          </div>
        </div>
      )}

      {tab === 'portal' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 bg-white border border-[#E2E2DF] rounded-md p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-semibold text-slate-800">{selectedClients.length} selected</span>
              <div className="flex gap-2"><button onClick={selectAllClients} className="text-xs text-[#D35400] font-semibold">Select All</button><button onClick={clearAll} className="text-xs text-slate-400">Clear</button></div>
            </div>
            <div className="relative mb-3"><Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" /><input value={searchCl} onChange={e => setSearchCl(e.target.value)} placeholder="Filter clients..." className="w-full pl-9 pr-3 py-2 border border-[#E2E2DF] rounded-md text-sm" /></div>
            <div className="max-h-96 overflow-y-auto space-y-1">
              {filteredClients.map(c => (
                <label key={c._id} className={`flex items-center gap-3 p-2 rounded cursor-pointer ${selectedClients.includes(c._id) ? 'bg-[#D35400]/5' : 'hover:bg-slate-50'}`}>
                  <input type="checkbox" checked={selectedClients.includes(c._id)} onChange={() => toggleClient(c._id)} className="rounded" />
                  <div className="flex-1 min-w-0"><p className="text-sm text-slate-800 truncate">{c.name_first} {c.name_last}</p><p className="text-[10px] text-slate-400">{c.email || 'No email'}</p></div>
                </label>
              ))}
            </div>
          </div>
          <div className="bg-white border border-[#E2E2DF] rounded-md p-4 space-y-4 h-fit">
            <h3 className="text-base font-semibold text-slate-800">Generate Portal Access</h3>
            <p className="text-sm text-slate-500">Generate access codes for selected clients who don't have one yet. Clients with existing access will be skipped.</p>
            <button data-testid="bulk-portal-generate" onClick={bulkPortal} disabled={processing || !selectedClients.length} className="w-full py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-md text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-2"><Key size={14} /> {processing ? 'Generating...' : `Generate for ${selectedClients.length} Clients`}</button>
          </div>
        </div>
      )}
    </div>
  );
}
